import { useState, useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Virtuoso } from 'react-virtuoso';
import { useAnalysisTestSelectionStore } from '@/store/useAnalysisTestSelectionStore';
import {
  Box,
  Typography,
  TextField,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { apiClient } from '@/lib/api';
import { useUser } from '@/store/useAuthStore';
import { canRetriggerClassification, formatTestStatus, isCalibrationEligible } from '@/lib/testStatus';

interface TestListItem {
  id: string;
  status: string;
  sampleCode?: string;
  profileName?: string;
  operatorName?: string;
  deviceNickname?: string;
  deviceUid?: string;
  startedAt?: string;
  completedAt?: string;
  notes?: string;
  importSourceBaseline?: string;
  importSourceSpecimen?: string;
}

interface PagedResult {
  items: TestListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface TestSelectorPanelProps {
  colorMap: Map<string, string>;
  /** On the Calibration tab: mark tests that calibration would leave out. */
  calibrationMode?: boolean;
}

const QUICK_LABELS = ['PBS Control', 'Pathogen', 'Suspect'] as const;
const PAGE_SIZE = 50;

/** Formats an ISO date string as compact date+time, e.g. "20/5/26, 14:33". Returns null if undefined. */
function formatTestLabel(iso?: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'numeric',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Returns a chip descriptor based on note content, or null if no match. */
export function getNoteChip(
  notes?: string,
): { label: string; color: 'info' | 'success' | 'warning' } | null {
  if (!notes) return null;
  const lower = notes.toLowerCase();
  if (lower.includes('pbs') || lower.includes('control')) return { label: 'PBS', color: 'info' };
  if (lower.includes('pathogen')) return { label: 'Pathogen', color: 'success' };
  if (lower.includes('suspect')) return { label: 'Suspect', color: 'warning' };
  return null;
}

const CHIP_COLORS: Record<string, 'info' | 'success' | 'warning'> = {
  'PBS Control': 'info',
  'Pathogen': 'success',
  'Suspect': 'warning',
};

/** Parses a notes string into the chip labels it starts with and the remaining free text. */
function parseNoteChips(notes?: string | null): { chips: string[]; remainder: string } {
  const chips: string[] = [];
  let text = notes ?? '';
  let found = true;
  while (found) {
    found = false;
    for (const label of QUICK_LABELS) {
      if (text === label) { chips.push(label); text = ''; found = true; break; }
      if (text.startsWith(label + ' ')) { chips.push(label); text = text.slice(label.length + 1); found = true; break; }
    }
  }
  return { chips, remainder: text };
}

export function TestSelectorPanel({ colorMap, calibrationMode = false }: TestSelectorPanelProps) {
  const user = useUser();
  const canManageClassification = user?.role === 'admin' || user?.role === 'supervisor';

  const browseId = useAnalysisTestSelectionStore((s) => s.browseId);
  const tickedIds = useAnalysisTestSelectionStore((s) => s.tickedIds);
  const hiddenIds = useAnalysisTestSelectionStore((s) => s.hiddenIds);
  const search = useAnalysisTestSelectionStore((s) => s.search);
  const deviceFilter = useAnalysisTestSelectionStore((s) => s.deviceFilter);
  const setBrowseId = useAnalysisTestSelectionStore((s) => s.setBrowseId);
  const setTickedIds = useAnalysisTestSelectionStore((s) => s.setTickedIds);
  const setSearch = useAnalysisTestSelectionStore((s) => s.setSearch);
  const setDeviceFilter = useAnalysisTestSelectionStore((s) => s.setDeviceFilter);
  const clearSelection = useAnalysisTestSelectionStore((s) => s.clearSelection);
  const hideTest = useAnalysisTestSelectionStore((s) => s.hideTest);
  const clearHiddenTests = useAnalysisTestSelectionStore((s) => s.clearHiddenTests);

  const [editingTestId, setEditingTestId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [editingChips, setEditingChips] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [retriggering, setRetriggering] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading, isFetchingNextPage, error, fetchNextPage, hasNextPage } =
    useInfiniteQuery<PagedResult>({
      queryKey: ['tests-for-analysis', search, deviceFilter],
      queryFn: async ({ pageParam }) => {
        const params: Record<string, unknown> = { pageSize: PAGE_SIZE, page: pageParam ?? 1 };
        if (search) params.search = search;
        const res = await apiClient.get<PagedResult>('/api/v1/tests', { params });
        return res.data;
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
      staleTime: 5 * 60 * 1000,
    });

  const allTests = useMemo(
    () => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );
  // Each page reports the current total, so the newest one is the most up to date.
  const totalCount = data?.pages[data.pages.length - 1]?.totalCount ?? 0;

  const hiddenSet = new Set(hiddenIds);

  const filtered = useMemo(
    () =>
      allTests.filter((t) => {
        if (hiddenSet.has(t.id)) return false;
        if (!deviceFilter) return true;
        const df = deviceFilter.toLowerCase();
        return (
          t.importSourceBaseline?.toLowerCase().includes(df) ||
          t.importSourceSpecimen?.toLowerCase().includes(df)
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allTests, deviceFilter, hiddenIds],
  );

  // Row click: browse mode → move highlight; combine mode → toggle tick.
  const handleRowClick = useCallback(
    (id: string) => {
      if (tickedIds.length === 0) {
        setBrowseId(id);
      } else {
        const next = tickedIds.includes(id)
          ? tickedIds.filter((x) => x !== id)
          : [...tickedIds, id];
        if (next.length === 0) {
          clearSelection();
        } else {
          setTickedIds(next);
        }
      }
    },
    [tickedIds, setBrowseId, setTickedIds, clearSelection],
  );

  // Checkbox click: always ticks/unticks, can enter combine mode from browse.
  const handleCheckboxClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const next = tickedIds.includes(id)
        ? tickedIds.filter((x) => x !== id)
        : [...tickedIds, id];
      setTickedIds(next);
    },
    [tickedIds, setTickedIds],
  );

  const handleSelectAll = async () => {
    const allIds: string[] = [];
    let page = 1;
    try {
      while (true) {
        const params: Record<string, unknown> = { pageSize: PAGE_SIZE, page };
        if (search) params.search = search;
        const res = await apiClient.get<PagedResult>('/api/v1/tests', { params });
        const pageIds = res.data.items
          .filter((t) => !hiddenSet.has(t.id))
          .map((t) => t.id);
        allIds.push(...pageIds);
        if (page >= res.data.totalPages) break;
        page++;
      }
      const combined = Array.from(new Set([...tickedIds, ...allIds]));
      setTickedIds(combined);
    } catch {
      // fallback to loaded items if the call fails
      const filteredIds = filtered.map((t) => t.id);
      const combined = Array.from(new Set([...tickedIds, ...filteredIds]));
      setTickedIds(combined);
    }
  };

  const handleClearAll = () => {
    clearSelection();
  };

  const handleEditStart = (test: TestListItem, e: React.MouseEvent) => {
    e.stopPropagation();
    const notes = test.notes ?? '';
    const chips = new Set<string>();
    let text = notes;

    // Peel known chip labels off the front of the saved string (our canonical format:
    // "Label1 Label2 free text"). Loop until no more labels can be stripped.
    let found = true;
    while (found) {
      found = false;
      for (const label of QUICK_LABELS) {
        if (text === label) {
          chips.add(label);
          text = '';
          found = true;
          break;
        }
        if (text.startsWith(label + ' ')) {
          chips.add(label);
          text = text.slice(label.length + 1);
          found = true;
          break;
        }
      }
    }

    setEditingTestId(test.id);
    setEditingChips(chips);
    setEditingValue(text);
  };

  const handleCancelEdit = () => {
    setEditingTestId(null);
    setEditingValue('');
    setEditingChips(new Set());
  };

  const handleToggleChip = (chipLabel: string) => {
    setEditingChips((prev) => {
      const next = new Set(prev);
      if (next.has(chipLabel)) {
        next.delete(chipLabel);
      } else {
        next.add(chipLabel);
      }
      return next;
    });
  };

  const handleSaveNotes = async (testId: string) => {
    setSaving(true);
    try {
      const chipPart = Array.from(editingChips).join(' ');
      const finalNotes = [chipPart, editingValue].filter(Boolean).join(' ').trim();
      await apiClient.patch(`/api/v1/tests/${testId}/notes`, { notes: finalNotes });
      await queryClient.invalidateQueries({ queryKey: ['tests-for-analysis'] });
      await queryClient.invalidateQueries({ queryKey: ['batch-spectra'] });
      setEditingTestId(null);
      setEditingValue('');
      setEditingChips(new Set());
    } finally {
      setSaving(false);
    }
  };

  const handleRetrigger = async (testId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRetriggering(testId);
    try {
      await apiClient.post(`/api/v1/tests/${testId}/classification/retrigger`);
      await queryClient.invalidateQueries({ queryKey: ['tests-for-analysis'] });
      await queryClient.invalidateQueries({ queryKey: ['test-detail', testId] });
    } finally {
      setRetriggering(null);
    }
  };

  const renderTestRow = useCallback(
    (test: TestListItem) => {
      const checked = tickedIds.includes(test.id);
      const isBrowsed = tickedIds.length === 0 && browseId === test.id;
      const color = colorMap.get(test.id);
      const { chips: noteChips, remainder: noteRemainder } = parseNoteChips(test.notes);
      const isEditing = editingTestId === test.id;
      const dateLabel = formatTestLabel(test.startedAt ?? test.completedAt);
      const deviceLabel = test.deviceNickname
        ? test.operatorName
          ? `${test.deviceNickname} (${test.operatorName})`
          : test.deviceNickname
        : (test.operatorName ?? test.deviceUid);
      const primaryLabel = [dateLabel, deviceLabel].filter(Boolean).join(' - ');
      const secondaryFallback = !test.notes ? (test.sampleCode ?? null) : null;
      const notCalibratable = calibrationMode && !isCalibrationEligible(test.status);

      return (
        <Box key={test.id}>
          <ListItem
            disablePadding
            secondaryAction={
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pr: 0.5 }}>
                {canManageClassification && canRetriggerClassification(test.status) && (
                  <Tooltip title="Re-trigger classification">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={(e) => void handleRetrigger(test.id, e)}
                        disabled={retriggering === test.id}
                        sx={{ p: 0.25, width: 22, height: 22 }}
                      >
                        {retriggering === test.id
                          ? <CircularProgress size={12} />
                          : <RestartAltIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Box>
                  </Tooltip>
                )}
                <Tooltip title="Hide test">
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); hideTest(test.id); }}
                    sx={{ p: 0.25, width: 22, height: 22 }}
                  >
                    <VisibilityOffIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit notes">
                  <IconButton
                    size="small"
                    onClick={(e) => handleEditStart(test, e)}
                    sx={{ p: 0.25, width: 22, height: 22 }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {color && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: color,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Stack>
            }
          >
            <ListItemButton
              onClick={() => handleRowClick(test.id)}
              selected={isBrowsed}
              dense
              sx={{ pr: 8, ...(notCalibratable && { '& .MuiListItemText-root': { opacity: 0.55 } }) }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Checkbox edge="start" checked={checked} size="small" disableRipple onClick={(e) => handleCheckboxClick(test.id, e)} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={
                  <Typography noWrap sx={{ fontSize: '0.78rem' }}>
                    {primaryLabel || test.id.slice(0, 8)}
                  </Typography>
                }
                secondary={
                  noteChips.length > 0 || noteRemainder || secondaryFallback || notCalibratable ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                      {notCalibratable && (
                        <Tooltip title="Calibration only uses tests whose baseline and specimen measurements both finished; this one will be left out.">
                          <Chip
                            label={formatTestStatus(test.status)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 16, fontSize: '0.6rem' }}
                          />
                        </Tooltip>
                      )}
                      {noteChips.map((chip) => (
                        <Chip
                          key={chip}
                          label={chip}
                          color={CHIP_COLORS[chip]}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      ))}
                      {(noteRemainder || secondaryFallback) && (
                        <Typography
                          sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                          noWrap
                        >
                          {noteRemainder || secondaryFallback}
                        </Typography>
                      )}
                    </Box>
                  ) : null
                }
              />
            </ListItemButton>
          </ListItem>

          {/* Inline annotation editor */}
          <Collapse in={isEditing} unmountOnExit>
            <Box sx={{ px: 1.5, pb: 1, bgcolor: 'action.hover' }}>
              {editingChips.size > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                  {Array.from(editingChips).map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      size="small"
                      onDelete={() => handleToggleChip(chip)}
                      sx={{ height: 24, fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
              )}
              <TextField
                size="small"
                fullWidth
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                placeholder="Add notes…"
                autoFocus
                disabled={saving}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleSaveNotes(test.id);
                  if (e.key === 'Escape') handleCancelEdit();
                }}
                sx={{ mt: 0.5 }}
              />
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {QUICK_LABELS.map((ql) => (
                  <Chip
                    key={ql}
                    label={ql}
                    size="small"
                    variant={editingChips.has(ql) ? 'filled' : 'outlined'}
                    onClick={() => handleToggleChip(ql)}
                    sx={{ height: 20, fontSize: '0.65rem', cursor: 'pointer' }}
                  />
                ))}
              </Stack>
              <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: 0.5 }}>
                <Button size="small" onClick={handleCancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleSaveNotes(test.id)}
                  disabled={saving}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </Box>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tickedIds, browseId, colorMap, editingTestId, editingChips, editingValue, saving, retriggering, canManageClassification, calibrationMode],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Filters */}
      <Box sx={{ px: 1, pt: 1, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search tests…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          fullWidth
        />
        <TextField
          size="small"
          placeholder="Source file filter…"
          value={deviceFilter}
          onChange={(e) => setDeviceFilter(e.target.value)}
          fullWidth
        />
      </Box>

      {/* Bulk actions */}
      <Box sx={{ px: 1, pb: 0.5, display: 'flex', gap: 1 }}>
        <Button size="small" onClick={() => void handleSelectAll()} disabled={filtered.length === 0}>
          All
        </Button>
        <Button size="small" onClick={handleClearAll} disabled={tickedIds.length === 0 && browseId === null}>
          Clear
        </Button>
        <Typography variant="caption" sx={{ ml: 'auto', alignSelf: 'center', color: 'text.secondary' }}>
          {tickedIds.length > 0 ? `${tickedIds.length} combined` : browseId ? 'browsing' : ''}
        </Typography>
      </Box>
      {totalCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 0.5 }}>
          {hasNextPage ? `Loaded ${allTests.length} of ${totalCount} — scroll for more` : `${totalCount} tests`}
        </Typography>
      )}
      {hiddenIds.length > 0 && (
        <Box sx={{ px: 1, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {hiddenIds.length} hidden
          </Typography>
          <Button size="small" onClick={clearHiddenTests} sx={{ minWidth: 0, px: 1 }}>
            Reset
          </Button>
        </Box>
      )}

      <Divider />

      {/* List */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 1 }}>
            Failed to load tests
          </Alert>
        )}
        {!isLoading && filtered.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No tests found
          </Typography>
        )}
        {!isLoading && filtered.length > 0 && (
          <Virtuoso
            style={{ flex: 1 }}
            data={filtered}
            endReached={() => {
              if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
            }}
            itemContent={(_index, test) => renderTestRow(test)}
            components={{
              Footer: () =>
                isFetchingNextPage ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : null,
            }}
          />
        )}
      </Box>
    </Box>
  );
}
